import type { TPlayer } from 'video.js'
import type { DolbyPlugin } from '|>/core/addons/dolby'
import type { AudioTrack, AudioTrackList } from '|>/shared/types/tracks'

import { logger } from '|>/shared/logger'
import { isDolbyTrack } from '|>/shared/utils/dolby'

export interface IAudioTrackItem {
  id: string
  label?: string
  language?: string
  kind?: string
  isDolby?: boolean
}

export const PUREYA_AUDIO_TRACK_STORAGE_KEY = 'pureya__audio_track'

export function getStoredAudioTrack(): IAudioTrackItem | null {
  try {
    const stored = window.localStorage.getItem(PUREYA_AUDIO_TRACK_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (err) {
    logger.warn(err)
    return null
  }
}

export function setStoredAudioTrack(track: IAudioTrackItem): void {
  try {
    window.localStorage.setItem(
      PUREYA_AUDIO_TRACK_STORAGE_KEY,
      JSON.stringify(track)
    )
  } catch (err) {
    logger.warn(err)
  }
}

export function clearStoredAudioTrack(): void {
  try {
    window.localStorage.removeItem(PUREYA_AUDIO_TRACK_STORAGE_KEY)
  } catch (err) {
    logger.warn(err)
  }
}

export function getBaseLanguage(lang: string): string {
  return lang.split('-')[0].toLowerCase()
}

export function isLanguageMatch({
  track,
  searchedLanguage,
  exactMatch = true,
}: {
  track: IAudioTrackItem
  searchedLanguage: string
  exactMatch?: boolean
}): boolean {
  if (!searchedLanguage || !track.language) return false

  if (exactMatch) {
    return track.language === searchedLanguage
  }

  const searchedBase = getBaseLanguage(searchedLanguage.toLowerCase())
  const trackBase = getBaseLanguage(track.language.toLowerCase())
  return trackBase.includes(searchedBase) || searchedBase.includes(trackBase)
}

function findTrackById(
  tracks: IAudioTrackItem[],
  id: string
): IAudioTrackItem | undefined {
  return tracks.find((t) => t.id === id)
}

function findExactAudioTrack(
  tracks: IAudioTrackItem[],
  stored: IAudioTrackItem
): IAudioTrackItem | undefined {
  return tracks.find(
    (t) =>
      isLanguageMatch({
        track: t,
        searchedLanguage: stored.language ?? '',
        exactMatch: true,
      }) &&
      stored.language &&
      t.isDolby === stored.isDolby
  )
}

function findBaseLanguageAudioTrack(
  tracks: IAudioTrackItem[],
  searchedLanguage: string | undefined | null,
  preferDolby: boolean
): IAudioTrackItem | undefined {
  return tracks.find((t) => {
    const isSameLang = isLanguageMatch({
      track: t,
      searchedLanguage: searchedLanguage ?? '',
      exactMatch: false,
    })
    if (!isSameLang) return false
    return preferDolby ? Boolean(t.isDolby) : !t.isDolby
  })
}

/**
 * A HLS main manifest can declare several AUDIO groups (e.g. one group per
 * codec/channel layout), and only the group paired (via the AUDIO attribute)
 * with the currently active video rendition is actually reachable: VHS
 * silently ignores `track.enabled` changes for tracks outside that group
 * (see `onTrackChanged` in @videojs/http-streaming media-groups.js), while
 * still playing whatever track it defaulted to for that group. Restricting
 * auto-selection to reachable tracks avoids picking one VHS will ignore.
 *
 * Returns null when reachability can't be determined (non-HLS, single
 * group, etc.), meaning no filtering should be applied.
 */
export function getActiveAudioGroupTrackIds(
  player: TPlayer | undefined
): Set<string> | null {
  try {
    const vhs = (player?.tech_ as any)?.vhs
    const groups = vhs?.playlistController_?.mediaTypes_?.AUDIO?.groups

    if (!groups || Object.keys(groups).length <= 1) return null

    const activeGroupId = vhs?.playlists?.media?.()?.attributes?.AUDIO
    const activeGroup = (activeGroupId && groups[activeGroupId]) || groups.main

    if (!activeGroup?.length) return null

    return new Set(activeGroup.map((entry: any) => entry.id))
  } catch (err) {
    logger.warn(err)
    return null
  }
}

function findAudioTrackByDolbyType(
  tracks: IAudioTrackItem[],
  isDolby: boolean
): IAudioTrackItem | undefined {
  return tracks.find((t) => t.isDolby === isDolby)
}

/**
 * Builds enriched track list with isDolby flag for each track.
 */
function buildEnrichedAudioTracks({
  audioTracks,
  dolbyEnabled,
  dolbyPlugin,
  isHls,
}: {
  audioTracks: AudioTrackList
  dolbyEnabled: boolean
  dolbyPlugin?: DolbyPlugin
  isHls: boolean
}): IAudioTrackItem[] {
  const enrichedTracks: IAudioTrackItem[] = []
  for (let i = 0; i < audioTracks.length; i++) {
    const track = audioTracks[i]
    let isDolby = false

    if (dolbyEnabled) {
      isDolby = isDolbyTrack({
        track,
        dolbyTracks: dolbyPlugin?.tracks,
        isHls,
      })
    }

    enrichedTracks.push({
      id: track.id,
      label: track.label,
      language: track.language,
      kind: track.kind,
      isDolby,
    })
  }
  return enrichedTracks
}

export function getStoredSelectedAudioTrack(
  tracks: IAudioTrackItem[]
): IAudioTrackItem | undefined {
  const stored = getStoredAudioTrack()
  if (!stored) return undefined

  // Priority order:
  // 1. Exact match
  // 2. Base language match with Dolby as selected
  // 3. Base language match with Dolby as reverse selected
  // 4. Any Dolby track (if selected Dolby)
  // 5. Any non-Dolby track (if selected non-Dolby)
  return (
    findExactAudioTrack(tracks, stored) ??
    findBaseLanguageAudioTrack(
      tracks,
      stored.language,
      stored.isDolby ?? false
    ) ??
    findBaseLanguageAudioTrack(
      tracks,
      stored.language,
      !(stored.isDolby ?? false)
    ) ??
    findAudioTrackByDolbyType(tracks, stored.isDolby ?? false) ??
    findAudioTrackByDolbyType(tracks, !(stored.isDolby ?? false))
  )
}

export function getLocaleSelectedAudioTrack(
  tracks: IAudioTrackItem[],
  locale: string | undefined | null,
  preferDolby: boolean
): IAudioTrackItem | undefined {
  if (!locale) return undefined

  // Priority order:
  // if preferDolby is true:
  // 1. Base language match with Dolby (if preferDolby)
  // 2. Base language match without Dolby (if not preferDolby)
  // if preferDolby is false:
  // 1. Base language match without Dolby
  if (preferDolby) {
    return (
      findBaseLanguageAudioTrack(tracks, locale, true) ??
      findBaseLanguageAudioTrack(tracks, locale, false)
    )
  }
  return findBaseLanguageAudioTrack(tracks, locale, false)
}

interface SetPreferableAudioTrackParams {
  player?: TPlayer
  audioTracks: AudioTrackList
  locale?: string
  preferDolbyAtmos?: boolean
  dolbyEnabled?: boolean
  dolbyPlugin?: DolbyPlugin
  isHls: boolean
  setTrack: (track: AudioTrack, tracks: AudioTrackList) => void
}

// Priority order:
// 1. Stored selected track, matched by exact id first, then by
//    language/Dolby heuristics restricted to the reachable audio group
// 2. App locale Base language match (reachable tracks only)
// 3. a. if preferDolbyAtmos: Any Dolby track / Any non-Dolby track
//    b. if not preferDolbyAtmos: Any non-Dolby track
//    (reachable tracks only)
// 4. Fallback to default track
export function setPreferableAudioTrack({
  player,
  audioTracks,
  locale,
  preferDolbyAtmos = false,
  dolbyEnabled = false,
  dolbyPlugin,
  isHls,
  setTrack,
}: SetPreferableAudioTrackParams): void {
  if (!audioTracks?.length) return

  const enrichedTracks = buildEnrichedAudioTracks({
    audioTracks,
    dolbyEnabled,
    dolbyPlugin,
    isHls,
  })

  const reachableIds = isHls ? getActiveAudioGroupTrackIds(player) : null
  const candidateTracks = reachableIds
    ? enrichedTracks.filter((t) => reachableIds.has(t.id))
    : enrichedTracks

  // Prefer the exact same track (by id) the user previously selected, even
  // if it's currently unreachable (see getActiveAudioGroupTrackIds above):
  // it's an unambiguous match, unlike the language/Dolby heuristics below,
  // which can mismatch tracks that share a language code but are otherwise
  // different (e.g. LANGUAGE="en" vs LANGUAGE="eng" both meaning English).
  // Only fall back to the heuristic match, restricted to reachable tracks,
  // when the originally selected track no longer exists.
  const stored = getStoredAudioTrack()
  const storedMatch =
    (stored && findTrackById(enrichedTracks, stored.id)) ??
    getStoredSelectedAudioTrack(candidateTracks)
  if (storedMatch) {
    const vjsTrack = findVideoJsTrack(audioTracks, storedMatch.id)
    if (vjsTrack) {
      setTrack(vjsTrack, audioTracks)
      return
    }
  }

  // 2. Locale match with Dolby preference
  if (locale) {
    const localeMatch = getLocaleSelectedAudioTrack(
      candidateTracks,
      locale,
      preferDolbyAtmos
    )
    if (localeMatch) {
      const vjsTrack = findVideoJsTrack(audioTracks, localeMatch.id)
      if (vjsTrack) {
        setTrack(vjsTrack, audioTracks)
        return
      }
    }
  }

  const dolbyTypeMatch =
    findAudioTrackByDolbyType(candidateTracks, preferDolbyAtmos) ??
    findAudioTrackByDolbyType(candidateTracks, !preferDolbyAtmos)

  if (dolbyTypeMatch) {
    const vjsTrack = findVideoJsTrack(audioTracks, dolbyTypeMatch.id)
    if (vjsTrack) {
      setTrack(vjsTrack, audioTracks)
      return
    }
  }

  const fallbackTrack = candidateTracks[0] ?? enrichedTracks[0]
  const vjsFallbackTrack =
    (fallbackTrack && findVideoJsTrack(audioTracks, fallbackTrack.id)) ??
    audioTracks[0]

  if (vjsFallbackTrack) {
    setTrack(vjsFallbackTrack, audioTracks)
  }
}

function findVideoJsTrack(
  audioTracks: AudioTrackList,
  id: string
): AudioTrack | undefined {
  if (!audioTracks) return undefined
  for (let i = 0; i < audioTracks.length; i++) {
    if (audioTracks[i].id === id) return audioTracks[i]
  }
  return undefined
}
